import React, { useEffect, useState } from "react";
import { Mail, Send, Loader2, Users, TrendingUp, Briefcase } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const AUDIENCES = [
  { key: "owners", label: "Owners", icon: Users },
  { key: "owner_leads", label: "Owner Leads", icon: TrendingUp },
  { key: "corporate", label: "Corporate Guests", icon: Briefcase },
];

export default function Newsletter() {
  const [audience, setAudience] = useState("owners");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [preview, setPreview] = useState(null);
  const load = () => api.get("/newsletters").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    setPreview(null);
    api.get(`/newsletters/recipients?audience=${audience}`).then((r) => setPreview(r.data)).catch(() => {});
  }, [audience]);

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and message are required"); return; }
    const n = preview ? preview.recipient_count : "all";
    if (!window.confirm(`Send this newsletter to ${n} ${AUDIENCES.find((a) => a.key === audience).label}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/newsletters", { audience, subject, body });
      toast.success(`Sent to ${data.recipient_count} recipient(s)`);
      setSubject(""); setBody(""); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };
  const audLabel = (k) => (AUDIENCES.find((a) => a.key === k) || {}).label || k;

  return (
    <div className="space-y-6">
      <div>
        <p className="overline text-accent">Broadcast</p>
        <h1 className="font-head text-3xl font-extrabold tracking-tight mt-1">Newsletter</h1>
        <p className="text-sm text-muted-foreground mt-1">Send an update to your Owners, Owner Leads or Corporate guests.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-border rounded-md p-5 space-y-3">
          <div>
            <Label className="overline">Audience</Label>
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger data-testid="newsletter-audience" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{AUDIENCES.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="overline">Subject</Label><Input data-testid="newsletter-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1.5" /></div>
          <div><Label className="overline">Message</Label><Textarea data-testid="newsletter-body" rows={9} value={body} onChange={(e) => setBody(e.target.value)} className="mt-1.5" placeholder="Write your update…" /></div>
          {preview && (
            <p data-testid="newsletter-preview" className="text-xs text-muted-foreground">
              Will send to <strong className="text-foreground">{preview.recipient_count}</strong> recipient(s){preview.opted_out > 0 ? ` · ${preview.opted_out} opted-out excluded` : ""}.
            </p>
          )}
          <Button data-testid="newsletter-send" onClick={send} disabled={sending || !subject.trim() || !body.trim() || (preview && preview.recipient_count === 0)} className="w-full">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" />Send newsletter</>}
          </Button>
        </div>

        <div className="bg-white border border-border rounded-md p-5">
          <p className="overline mb-3 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />Sent history</p>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No newsletters sent yet.</p>
          ) : (
            <div className="space-y-2">
              {history.map((n) => (
                <div key={n.id} data-testid={`newsletter-${n.id}`} className="border border-border rounded-md p-3">
                  <p className="text-sm font-semibold">{n.subject}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{audLabel(n.audience)} · {n.recipient_count} recipient(s) · {fmtDateTime(n.sent_at)}{n.sent_by ? ` · ${n.sent_by}` : ""}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
